import type {
  MobileFrontEndCardView,
  MobileUiConfigArtifact,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { humanize } from './columns';
import { ARTIFACT_ID_FIELD } from './constants';
import { artifactTableView } from './labels';
import { isSelectableFieldPath, resolvePath } from './message-mapping';
import { buildPagedQuery } from './selection-set';
import { orderByIfSortable } from './where';

/** One labelled value on a card. */
export interface CardDetail {
  field: string;
  label: string;
  value: string;
}

export interface CardItem {
  id: string;
  title: string;
  subtitle: string | null;
  /** Avatar image URL, when the artifact carries one. */
  avatarUrl: string | null;
  /** Initials shown when there is no avatar image. */
  initials: string;
  badges: string[];
  details: CardDetail[];
  row: Record<string, unknown>;
}

/**
 * Fields a card page selects.
 *
 * When the view declares no `detail_fields`, the table view's default columns
 * stand in — those are already the curated "what matters about this row" set, so
 * a card view is cheap to author and still useful before anyone tunes it.
 */
export function cardFields(
  view: MobileFrontEndCardView,
  artifact: MobileUiConfigArtifact | undefined,
): string[] {
  const fields = new Set<string>([ARTIFACT_ID_FIELD, view.title_field]);
  if (view.subtitle_field) fields.add(view.subtitle_field);
  if (view.avatar_field) fields.add(view.avatar_field);
  for (const field of view.badge_fields ?? []) fields.add(field);
  for (const field of cardDetailFields(view, artifact)) fields.add(field);
  return [...fields].filter((field) => isSelectableFieldPath(artifact, field));
}

/** Ordered body fields for a card. */
export function cardDetailFields(
  view: MobileFrontEndCardView,
  artifact: MobileUiConfigArtifact | undefined,
): string[] {
  const declared = view.detail_fields ?? [];
  if (declared.length) return declared.filter((f) => isSelectableFieldPath(artifact, f));

  const fallback = artifactTableView(artifact)?.default_columns ?? [];
  return fallback
    .filter((field) => field !== view.title_field && field !== view.subtitle_field)
    .filter((field) => isSelectableFieldPath(artifact, field))
    .slice(0, 4);
}

/** One page of cards, ordered by title so the grid reads alphabetically. */
export function buildCardsQuery(args: {
  rootQuery: string;
  view: MobileFrontEndCardView;
  artifact: MobileUiConfigArtifact | undefined;
  mobileAcquisitionId: number;
  offset: number;
  limit: number;
  where?: Record<string, unknown> | null;
}): string | null {
  const { view } = args;
  return buildPagedQuery({
    operationName: 'ArtifactCards',
    rootQuery: args.rootQuery,
    mobileAcquisitionId: args.mobileAcquisitionId,
    offset: args.offset,
    limit: args.limit,
    fields: cardFields(view, args.artifact),
    where: args.where,
    // Title is what a card grid reads against, but many types cannot sort on
    // it. Skipping rather than guessing keeps the query valid; the page is
    // small enough to order in the client if we ever need to.
    orderBy: orderByIfSortable(view.title_field, 'asc', artifactTableView(args.artifact)),
  });
}

/** Map rows into cards, using the ui-config labels for detail rows. */
export function mapCardItems(
  rows: Record<string, unknown>[],
  args: {
    view: MobileFrontEndCardView;
    artifact: MobileUiConfigArtifact | undefined;
    uiConfig: MobileUiConfigResponse | undefined;
  },
): CardItem[] {
  const { view, artifact } = args;
  const detailFields = cardDetailFields(view, artifact);
  const labels = columnLabels(artifact);

  return rows.map((row, index) => {
    const title = asText(resolvePath(row, view.title_field)) ?? 'Unnamed';
    const avatar = view.avatar_field ? asText(resolvePath(row, view.avatar_field)) : null;

    return {
      id: String(resolvePath(row, ARTIFACT_ID_FIELD) ?? index),
      title,
      subtitle: view.subtitle_field ? asText(resolvePath(row, view.subtitle_field)) : null,
      avatarUrl: avatar && /^(https?:|s3:)/i.test(avatar) ? avatar : null,
      initials: initialsFor(title),
      badges: (view.badge_fields ?? [])
        .map((field) => asText(resolvePath(row, field)))
        .filter((value): value is string => Boolean(value)),
      details: detailFields
        .map((field) => ({
          field,
          label: labels.get(field) ?? humanize(field.split('.').pop() ?? field),
          value: asText(resolvePath(row, field)) ?? '',
        }))
        .filter((detail) => detail.value !== ''),
      row,
    };
  });
}

function columnLabels(artifact: MobileUiConfigArtifact | undefined): Map<string, string> {
  const labels = new Map<string, string>();
  for (const override of artifactTableView(artifact)?.column_overrides ?? []) {
    if (override.label) labels.set(override.field, override.label);
  }
  return labels;
}

/** Up to two initials from a display name; falls back to its first character. */
export function initialsFor(title: string): string {
  const words = title
    .split(/[\s._-]+/)
    .map((word) => word.trim())
    .filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

function asText(value: unknown): string | null {
  if (value == null) return null;
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}
